import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, Card, message, Select, Row, Col } from 'antd';
import { VideoCameraOutlined, AudioOutlined, ShareAltOutlined } from '@ant-design/icons';
import {
  ConsoleLogger,
  LogLevel,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  DefaultDeviceController,
} from 'amazon-chime-sdk-js';
import * as assessmentActions from '../../../store/actions/AssessmentActions';
import * as streamActions from '../../../store/actions/AssesmentRecordingActions';
import determineWorkflow from '../../../Utils/determineWorkflow';
import { useHistory } from 'react-router-dom';
import styles from './MediaPermissionComponent.scss';
const { Title } = Typography;

export default function MediaPermissionComponent({ assessment_id, recoveryMode }) {
  const finalAssessmentId = recoveryMode ? sessionStorage.getItem('assessment_id') : assessment_id;
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    videoStream,
    screenStream,
    hasVideo,
    hasScreen,
    error,
    videoInputs,
    audioInputs,
    selectedVideoInput,
    selectedAudioInput,
    deviceController,
  } = useSelector(state => state.assesmentRecording);
  const {
    meetingData,
    problems,
    mcqIds,
    sections,
    id: assessmentId,
    currentSectionId,
  } = useSelector(state => state.assessment);
  const localVideoRef = useRef(null);
  const initDevices = async () => {
    try {
      const logger = new ConsoleLogger('ChimeLogger', LogLevel.ERROR);
      const controller = new DefaultDeviceController(logger);
      await dispatch(streamActions.initDevices(controller));
      await dispatch(streamActions.initVideoStream());
    } catch {
      message.error('Camera/Microphone permission is required');
    }
  };

  const setUpMeeting = async () => {
    await dispatch(assessmentActions.getMeeting(finalAssessmentId));
  };

  useEffect(() => {
    setUpMeeting();
    initDevices();
  }, []);

  useEffect(() => {
    if (localVideoRef.current && videoStream) {
      localVideoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  useEffect(() => {
    if (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      message.error(errorMessage, 10);
    }
  }, [error]);

  const handleScreenShare = async () => {
    try {
      dispatch(streamActions.setScreenShareInitiated());
      await dispatch(streamActions.initScreenStream());
      setTimeout(() => {
        dispatch(streamActions.clearScreenShareInitiated());
      }, 3000);
    } catch (error) {
      dispatch(streamActions.clearScreenShareInitiated());
    }
  };

  const handleDeviceChange = (kind, deviceId) => {
    dispatch(streamActions.setSelectedDevice(kind, deviceId));
  };

  const onContinue = async () => {
    if (!hasVideo || !hasScreen) {
      message.error('Please enable camera, microphone, and screen sharing', 10);
      return;
    }
    const meetingData = await dispatch(assessmentActions.getMeeting(finalAssessmentId));
    const attendeeData = await dispatch(
      assessmentActions.getAttendee(meetingData.id, finalAssessmentId),
    );

    const configuration = new MeetingSessionConfiguration(
      {
        MeetingId: meetingData.aws_meeting_id,
        ExternalMeetingId: meetingData.id,
        MediaPlacement: meetingData.aws_media_placement,
      },
      {
        AttendeeId: attendeeData.aws_attendee_id,
        ExternalUserId: attendeeData.participant_id,
        JoinToken: attendeeData.aws_join_token,
      },
    );

    const logger = new ConsoleLogger('ChimeLogger', LogLevel.ERROR);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
    const audioVideo = meetingSession.audioVideo;

    dispatch(streamActions.setMeetingSession(meetingSession));

    if (selectedAudioInput) {
      await audioVideo.startAudioInput(selectedAudioInput);
    }
    if (selectedVideoInput) {
      await audioVideo.startVideoInput(selectedVideoInput);
    }

    if (screenStream) {
      setTimeout(async () => {
        try {
          await audioVideo.startContentShare(screenStream);
        } catch (error) {
          console.log('Content share start error:', error);
        }
      }, 1000);
    }

    await audioVideo.start();
    await audioVideo.startLocalVideoTile();

    if (recoveryMode) {
      // In recovery mode, just close the modal without routing
      dispatch(streamActions.closeMediaRecoveryModal());
    } else {
      await dispatch(assessmentActions.startRecording(meetingData.id));

      // Normal flow: start workflow and route
      const startSection = sectionId => {
        dispatch(assessmentActions.updateSectionStatus(sectionId, 'in_progress'));
      };

      determineWorkflow({
        sections,
        problems,
        mcqIds,
        assessmentId,
        routerHistory: history,
        shouldRoute: true,
        currentSectionId,
        startSection,
      });
    }
  };

  useEffect(() => {
    const handleCleanup = () => {
      dispatch(streamActions.stopVideoStream());
      dispatch(streamActions.stopScreenStream());
    };
    window.cleanupMediaStreams = handleCleanup;
    window.addEventListener('beforeunload', handleCleanup);
    return () => window.removeEventListener('beforeunload', handleCleanup);
  }, [dispatch]);

  return (
    <div
      className={
        recoveryMode
          ? `${styles['overlay-media-permission-container']} ${styles['media-permission-container']}`
          : styles['media-permission-container']
      }
    >
      <Card className={styles['media-permission-card']}>
        <div className={styles['media-permission-header']}>
          <Title
            level={3}
            className={styles['media-permission-title']}
          >
            Camera and Screen Share Access
          </Title>
          <Typography.Text className={styles['media-permission-subtitle']}>
            To ensure test integrity, we require access to your webcam, microphone, and screen
            sharing during the assessment.
          </Typography.Text>
        </div>
        <div className={styles['media-permission-content']}>
          <Row gutter={32}>
            {/* Left side - Camera */}
            <Col
              span={12}
              className={styles['media-permission-left-column']}
            >
              <div className={styles['media-permission-section']}>
                <div className={styles['media-permission-icon-section']}>
                  <VideoCameraOutlined style={{ marginRight: 8, color: '#666' }} />
                  <Typography.Text strong>Camera</Typography.Text>
                </div>
                <div className={styles['media-permission-video-container']}>
                  <video
                    ref={localVideoRef}
                    className={styles['media-permission-video']}
                    autoPlay
                    muted
                    playsInline
                  />
                  {!hasVideo && (
                    <div className={styles['media-permission-video-overlay']}>
                      <VideoCameraOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                      <div>Camera Preview</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={styles['media-permission-label']}>Camera Device</label>
                  <Select
                    className={styles['media-permission-select']}
                    placeholder="Select camera"
                    value={selectedVideoInput || undefined}
                    onChange={value => handleDeviceChange('video', value)}
                  >
                    {videoInputs.map(device => (
                      <Select.Option
                        key={device.deviceId}
                        value={device.deviceId}
                      >
                        {device.label || `Camera (${device.deviceId})`}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </Col>
            {/* Right side - Microphone + Screen */}
            <Col
              span={12}
              className={styles['media-permission-right-column']}
            >
              <div className={styles['media-permission-mic-section']}>
                <div className={styles['media-permission-icon-section']}>
                  <AudioOutlined style={{ marginRight: 8, color: '#666' }} />
                  <Typography.Text strong>Microphone</Typography.Text>
                </div>
                <div className={styles['media-permission-section']}>
                  <label className={styles['media-permission-label']}>Microphone Device</label>
                  <Select
                    className={styles['media-permission-select']}
                    placeholder="Select microphone"
                    value={selectedAudioInput || undefined}
                    onChange={value => handleDeviceChange('audio', value)}
                  >
                    {audioInputs.map(device => (
                      <Select.Option
                        key={device.deviceId}
                        value={device.deviceId}
                      >
                        {device.label || `Microphone (${device.deviceId})`}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div>
                <div className={styles['media-permission-icon-section']}>
                  <ShareAltOutlined style={{ marginRight: 8, color: '#666' }} />
                  <Typography.Text strong>Share Screen</Typography.Text>
                </div>
                <div className={styles['media-permission-screen-section']}>
                  <Button
                    type="primary"
                    className={styles['media-permission-button']}
                    onClick={handleScreenShare}
                    disabled={hasScreen}
                  >
                    {hasScreen ? 'Screen Sharing Enabled' : 'Allow Screen Sharing'}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <Button
          type="default"
          size="large"
          disabled={!hasVideo || !hasScreen}
          onClick={onContinue}
        >
          Continue
        </Button>
      </Card>
    </div>
  );
}
